import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SERIF, UI } from '../theme';
import { isLoggedIn } from '../../v2/api';
import { getStoredPersona, DEFAULT_PERSONA } from '../../v2/persona';

// FabSheet shows different actions per user mode so guests / companions
// aren't pushed toward owner-only flows (report lost, request blood, etc.)
// that would land them on broken/login-gated screens.
export default function FabSheet({ theme, onClose, onAction }) {
  const navigate = useNavigate();
  const personaId = getStoredPersona() || DEFAULT_PERSONA;
  // "Owner mode" = the user has self-identified as a pet owner. Everyone
  // else (guests, companions who chose "อยากช่วยและเรียนรู้", caretakers,
  // spotters) sees the discovery action set instead.
  const ownerMode = isLoggedIn() && personaId === 'owner';

  const ownerItems = [
    { id: 'lost',  icon: '🚨', label: 'แจ้งน้องหาย',  sub: 'รายงาน lost pet',   color: theme.alert },
    { id: 'blood', icon: '🩸', label: 'ขอเลือดด่วน',  sub: 'ติดต่อโรงพยาบาล',   color: theme.alert },
    { id: 'walk',  icon: '🦮', label: 'เริ่มเดินเล่น', sub: 'check-in ทันที',     color: theme.safe },
    { id: 'photo', icon: '📸', label: 'เจอน้องน่ารัก',  sub: 'ถ่าย แชร์ หรือปักหมุด', color: theme.primary },
  ];

  const discoverItems = [
    { id: 'swipe',    icon: '🎴', label: 'ลองเลือกน้อง', sub: 'เจอน้องที่ถูกใจ',   color: theme.primary, route: '/v2/community' },
    { id: 'map',      icon: '🗺️', label: 'ดูแผนที่',     sub: 'น้องในย่านคุณ',     color: theme.info,    route: '/v2/map' },
    { id: 'spot',     icon: '📸', label: 'เจอน้องน่ารัก', sub: 'ถ่าย แชร์ หรือปักหมุด', color: theme.accent,  route: '/v2/spot' },
    isLoggedIn()
      ? { id: 'add-pet', icon: '🐾', label: 'เพิ่มน้องของคุณ', sub: 'สร้างโปรไฟล์', color: theme.safe, route: '/v2/onboarding' }
      : { id: 'login',   icon: '🔓', label: 'เข้าสู่ระบบ',     sub: 'เก็บน้องที่ชอบ', color: theme.safe, route: '/v2/me' },
  ];

  const items = ownerMode ? ownerItems : discoverItems;
  const title = ownerMode ? 'วันนี้น้องอยากทำอะไร?' : 'อยากค้นพบอะไรดี?';

  return (
    <div
      data-testid="v2-fab-sheet"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="tl-v2-slide-up"
        style={{
          position: 'fixed', left: 0, right: 0, bottom: 0,
          background: theme.surface, color: theme.text,
          borderRadius: '28px 28px 0 0',
          padding: '12px 16px calc(96px + env(safe-area-inset-bottom))',
          maxWidth: 540, width: '100%', margin: '0 auto',
          maxHeight: 'calc(100dvh - 18px)',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, background: theme.muted, opacity: 0.4, margin: '4px auto 18px' }}/>
        <div style={{ fontFamily: SERIF, fontSize: 22, fontWeight: 600, padding: '0 6px 14px' }}>
          {title}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {items.map(it => (
            <button
              key={it.id}
              data-testid={`v2-fab-action-${it.id}`}
              onClick={() => {
                if (it.route) {
                  navigate(it.route);
                } else {
                  onAction?.(it.id);
                }
                onClose();
              }}
              style={{
                padding: 16, borderRadius: 22, border: `1px solid ${theme.cardBorder}`,
                background: theme.bg, textAlign: 'left', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start',
              }}
            >
              <div style={{
                width: 44, height: 44, borderRadius: 14,
                background: `${it.color}22`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
              }}>{it.icon}</div>
              <div style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 600, color: theme.text }}>{it.label}</div>
              <div style={{ fontFamily: UI, fontSize: 11, color: theme.muted }}>{it.sub}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
